import React, { useCallback } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { FieldWrapper } from "@progress/kendo-react-form";
import { Error } from "@progress/kendo-react-labels";
import { EXPRESSION_LABELS } from "../../constants/expressionBuilder.constants";
import "../../styles/ExpressionBuilder.css";
import { getOperatorsByType } from "../../utils/expressionBuilder.utils";
import FieldSelection from "./FieldSelection/FieldSelection";
import OperatorSelection from "./OperatorSelection/OperatorSelection";
import ValueInput from "./ValueInput/ValueInput";

/**
 * Component for rendering a single rule in the expression
 * @param {Object} rule - Rule configuration
 * @param {Function} onUpdate - Update handler
 * @param {Function} onRemove - Remove handler
 * @param {Array} availableFields - Available workflow variables
 * @param {Object} fieldValidators - Validation functions for fields
 * @returns {JSX.Element} Rule component
 * @author Abhishek Dhillon
 * @parent GroupComponent
 */
const RuleComponent = ({ rule, onUpdate, onRemove, availableFields = [], fieldValidators = {} }) => {
  const selectedField = availableFields.find((f) => f.id === rule?.field?.id);
  const fieldType = selectedField?.type || "text";
  const operators = getOperatorsByType(fieldType);

  /**
   * Handles field selection change
   * @param {Object} newField - New field object
   * @author Abhishek Dhillon
   */
  const handleFieldChange = (newField) => {
    // Single state update to prevent race conditions
    const updatedRule = {
      ...rule,
      field: {
        id: newField?.id,
        name: newField?.name,
        chipType: newField?.chipType,
        adminType: newField?.adminType,
        group: newField?.group,
        linkedVarName: newField?.linkedVarName,
      },
    };

    // Reset operator and value when field changes
    if (newField?.type) {
      const newOperators = getOperatorsByType(newField?.type);
      updatedRule.operator = newOperators[0] || {};
      updatedRule.value = "";
    }

    onUpdate(updatedRule);
  };

  /**
   * Handles operator selection change
   * @param {string} newOperator - New operator value
   * @author Abhishek Dhillon
   */
  const handleOperatorChange = (newOperator) => {
    // Single state update
    onUpdate({
      ...rule,
      operator: newOperator,
      // Clear value if switching to standard function
      value:
        newOperator === "IS_BLANK" || newOperator === "IS_NOT_BLANK" || newOperator === "IS_ZERO" || newOperator === "IS_NOT_ZERO" ? "" : rule.value,
    });
  };

  /**
   * Handles value input change
   * @param {*} newValue - New value
   * @author Abhishek Dhillon
   */
  const handleValueChange = (newValue) => {
    onUpdate({
      ...rule,
      value: newValue,
    });
  };

  /**
   * Renders field selection with validation
   * @returns {JSX.Element} Field selection with validation
   * @author Abhishek Dhillon
   */
  const renderFieldSelection = () => {
    const validationError = fieldValidators.field(rule?.field);
    const showValidationMessage = !!validationError;

    return (
      <div>
        <FieldSelection
          availableFields={availableFields}
          selectedField={rule?.field}
          onFieldChange={handleFieldChange}
          fieldType={fieldType}
          hasError={showValidationMessage}
        />
        {showValidationMessage && <Error>{validationError}</Error>}
      </div>
    );
  };

  /**
   * Renders operator selection with validation
   * @returns {JSX.Element} Operator selection with validation
   * @author Abhishek Dhillon
   */
  const renderOperatorSelection = () => {
    const validationError = fieldValidators.operator(rule?.operator);
    const showValidationMessage = !!validationError;

    return (
      <div>
        <OperatorSelection
          fieldType={fieldType}
          selectedOperator={rule?.operator}
          onOperatorChange={handleOperatorChange}
          showHint={!!selectedField}
          hasError={showValidationMessage}
        />
        {showValidationMessage && <Error>{validationError}</Error>}
      </div>
    );
  };

  /**
   * Renders value input with validation
   * @param {Object} fieldRenderProps - Kendo field render props
   * @returns {JSX.Element} Value input with validation
   * @author Abhishek Dhillon
   */
  const renderValueInput = () => {
    const validationError = fieldValidators.value(rule?.value, rule);
    const showValidationMessage = !!validationError;

    return (
      <div>
        <ValueInput
          fieldType={fieldType}
          value={rule?.value}
          onValueChange={handleValueChange}
          selectedField={selectedField}
          selectedOperator={rule?.operator}
          showHint={!!selectedField}
          hasError={showValidationMessage}
          availableFields={availableFields}
        />
        {showValidationMessage && <Error>{validationError}</Error>}
      </div>
    );
  };

  return (
    <div className='expression-builder-rule'>
      <div className='expression-builder-rule-container'>
        {/* Field Selection Component with Validation */}
        <div className='expression-builder-rule-field'>{renderFieldSelection()}</div>

        {/* Operator Selection Component with Validation */}
        <div className='expression-builder-rule-operator'>{renderOperatorSelection()}</div>

        {/* Value Input Component with Validation */}
        <div className='expression-builder-rule-value'>{renderValueInput()}</div>

        {/* Delete Button */}
        <div className='expression-builder-rule-delete'>
          <Button
            onClick={onRemove}
            icon='x'
            fillMode='flat'
            themeColor='error'
            title={EXPRESSION_LABELS.DELETE_RULE}
            id={`delete-rule-${rule?.id}`}
          />
        </div>
      </div>
    </div>
  );
};

export default RuleComponent;
